import { CustomError } from 'ts-custom-error';
import { formatUrl } from 'utilities/fetch/url.js';
import { messageLog } from '../helpers/logger/message-logger.js';

type requestParameters = {
  path: string;
  options?: RequestInit;
  /** Prefix with public/data */
  apiPrefix: boolean;
};
export enum FetchError {
  NETWORK_ERROR = 'network-error',
  SERVER_RESPONSE_ERROR = 'server-response-error',
  COMPONENT_NOT_FOUND = 'component-not-found',
  JSON_COMPILE_ERROR = 'json-compile-error',
}

export class RequestError extends CustomError {
  public constructor(
    public code: number,
    public type: FetchError,
    message?: string,
  ) {
    super(message);
    this.type = type;
    this.code = code;
  }
}

export type MakeRequest<T> = (params: requestParameters) => Promise<T>;

/**
 * Make fetch request
 * @param params
 */
export const makeRequest = async <T>({ apiPrefix, path, options = {} }: requestParameters): Promise<T> => {
  const finalUrl = formatUrl({ path, apiPrefix });
  const headerConfig = options.headers || {};
  const headers = new Headers(headerConfig);
  const credentials = options.credentials;

  const finalOptions = {
    ...options,
    headers,
    credentials,
  };
  try {
    const response = await fetch(finalUrl, finalOptions);
    if (response.ok) {
      const responseJson = await response.json();
      return responseJson;
    } else {
      // Server Response Error
      throw new RequestError(response.status, FetchError.NETWORK_ERROR, `${response.status}: ${response.statusText}`);
    }
  } catch (e) {
    if (e instanceof Error) {
      messageLog.error(e.message);
      throw e;
    }
    if (e instanceof RequestError) {
      if (e.type === FetchError.SERVER_RESPONSE_ERROR) {
        throw e;
      }
      /**
       * Fetch will treat all responses as OK, except for network failure which goes straight into the catch.
       * You can tell if its successful using the res.ok value for all other responses, like 2xx  3xx and 4xx
       */
      throw new RequestError(e.code, FetchError.NETWORK_ERROR, `${e?.message}: ${finalUrl}`);
    }
    console.log('Error is not a RequestError', e, finalUrl);
    throw new RequestError(404, FetchError.NETWORK_ERROR, `${JSON.stringify(e)}: ${finalUrl}`);
  }
};
